import { z, ZodType } from 'zod';

/**
 * ADR-002: Structured API error.
 * `code` is the stable machine-readable identifier (snake_case).
 * `message` is the human-readable display text (localized in future).
 * `details` carries optional structured context without PII.
 */
export const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

/**
 * ADR-002: Standard API response envelope.
 * All API endpoints return { data, error } shape.
 */
export function apiEnvelope<T extends ZodType>(dataSchema: T) {
  return z.object({
    data: dataSchema.nullable(),
    error: apiErrorSchema.nullable(),
  });
}

/** Schema-derived output shape shared by API envelope consumers. */
export type ApiEnvelope<T> = z.infer<ReturnType<typeof apiEnvelope<ZodType<T>>>>;

/**
 * Create a success response conforming to ADR-002.
 */
export function success<T>(data: T) {
  return { data, error: null };
}

/**
 * Create an error response conforming to ADR-002.
 * ADR-002: No PII in error messages.
 * @param code Stable machine-readable identifier (snake_case)
 * @param message Human-readable display text
 * @param details Optional structured context
 */
export function error(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): { data: null; error: ApiError } {
  // Annotated rather than inferred: a conditional spread infers `details` away
  // when the argument is absent, so callers type-checking a response body
  // could not see a field the schema has always declared.
  return { data: null, error: { code, message, ...(details ? { details } : {}) } };
}

/**
 * Read a finite, non-negative number out of an ADR-002 error envelope's
 * `details` bag — e.g. `retryAfterSeconds` on a 429.
 *
 * `details` is `Record<string, unknown>` by construction, so every consumer
 * otherwise re-implements the same three-part guard; it lives here so the
 * envelope's shape and the code that reads it stay in one place.
 */
export function readNumberDetail(
  details: Record<string, unknown> | undefined,
  key: string,
): number | undefined {
  const raw = details?.[key];
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : undefined;
}
